import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';

import EChartsReact from 'echarts-for-react';

import { fetchOverAllScore as fetchOverAllScoreService} from "services/modules/vapt/data";


const Graph = ({ reportDate,categoryId,subcategory }) => {
  // useLocation hook
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dimension= searchParams.get("dimension");
  let category = ''
  if(!dimension)
    category="over-all"
  else
    category=dimension;
   
  // State variables
  const [series, setSeries] = useState(0);
  const [securityLevel, setSecurityLevel] = useState('Informational');
  const [securityColor, setSecurityColor] = useState('#008FFB');
  const [finalScore, setfinalScore] = useState(0);
  
  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const dimensionsContainer = useSelector(state => state.vaptData.dimensions);
  const severitiesContainer = useSelector(state => state.vaptData.severities);
  
  
  // This method returns the severity based on score
  function getSeverity(score) {
    const severe = severitiesContainer?.find((severity) => {
      let allowed = true;
      if (severity.range.gte !== undefined && allowed) {
        allowed = score >= severity.range.gte;
      }
      if (severity.range.lte !== undefined && allowed) {
        allowed = score <= severity.range.lte;
      }
      if (severity.range.gt !== undefined && allowed) {
        allowed = score > severity.range.gt;
      }
      if (severity.range.lt !== undefined && allowed) {
        allowed = score < severity.range.lt;
      }
      return allowed;
    });
    return severe;
  };
  
  // This method returns security color based on severity slug
  function getSecurityColor(slug) {
    switch (slug) {
      case "informational":
        return "#2fc430";
      case "low":
        return "#4baeec";
      case "medium":
        return "#4c4ccc";
      case "high":
        return "#fc659c";
      case "critical":
        return "#eb4b2c";
      default:
        return "#008FFB";
    }
  };
  
  // This method returns emoji based on final score
  function getEmoji(value) {
    switch (true) {
      case value === 0:
        return require("../../../../assets/emoji_1.png");
      case value <= 3.99:
        return require("../../../../assets/emoji_2.png");
      case value <= 6.99:
        return require("../../../../assets/emoji_3.png");
      case value <= 8.99:
        return require("../../../../assets/emoji_4.png");
      case value <= 10:
        return require("../../../../assets/emoji_5.png");
      default:
        return require("../../../../assets/emoji_1.png");
    }
  }
  
  // useEffect hook fetches the overall score data and sets the final score
  useEffect(() => {
    const fetchOverAllScoreData = async() => {
      let params = {
        "domain": selectedCompanyContainer.domain,
        "date": reportDate,
        "category": categoryId,
        "subcategory": subcategory
      }
      if ((category !== "over-all") && (category !== "compliance")){
        const dimension = dimensionsContainer.find((dim) => dim.slug === category);
        params.dimension = dimension.id;
      }
      const response = await fetchOverAllScoreService({ params });
      setfinalScore(response.score)
    };
    if (reportDate && (dimensionsContainer.length > 0) && categoryId){
      fetchOverAllScoreData();
    } else {
      setfinalScore(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDate, category, dimensionsContainer]);
  
  // Guage options
  let guageOptions = {
    tooltip: {
      responsive: true,
      position: "top",
      formatter: params => {
        const value = params.value
        if (value === 0){
          return 'A'
        } else if (value > 0 && value < 3.99) {
          return 'B'
        } else if (value >= 4 && value < 6.99) {
          return 'C'
        } else if (value >= 7 && value < 8.99) {
          return 'D'
        } else if (value >= 9) {
          return 'F'
        } else {
          return 'A'
        }
      },
      padding: 5,
      transitionDuration: 0,
      hideDelay: 100,
      backgroundColor: "#fff",
      borderColor: "#c8e2f7",
      borderWidth: "0.2",
      textStyle: {
        color: "#414143",
        fontSize: 14,
        fontFamily: "Montserrat",
        fontWeight: 600,
      },
    },
    series: [
      {
        name: "Risk Posture",
        type: "gauge",
        startAngle: 200,
        endAngle: -20,
        min: 1,
        max: 10,
        splitNumber: 5,
        radius: "80%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 40,
            color: [
              [0.2, "#41914d"],
              [0.4, "#9ac356"],
              [0.6, "#fcdd50"],
              [0.8, "#e56c39"],
              [1, "#db3832"],
            ],
          },
        },
        pointer: {
          width: 12,
          length: "50%",
          distance: -500,
          offsetCenter: [0, "-15%"],
          itemStyle: {
            color: "#414143",
            shadowBlur: 5,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          distance: -200,
          length: 201,
          lineStyle: {
            color: "#f8f9fc",
            width: 5,
          },
        },
        axisLabel: {
          color: "#fff",
          distance: 200,
          fontSize: 20,
          show: false,
        },
        detail: {
          show: true,
          valueAnimation: true,
          formatter: function () {
            return "";
          },
          color: "inherit",
          backgroundColor: {
            image: getEmoji(finalScore),
          },
          height: 35,
          width: 25,
          offsetCenter: [0, "32%"],
        },
        title: {
          offsetCenter: [0, '80%'],
          fontSize: 18,
          fontWeight: 'Bold',
          color: '#0e0a65'
        },
        data: [
          {
            value: series,
            name: "Overall Rating",
          },
        ],
      },
    ],
  };
  
  // useEffect hook calculates the series value based on final score and updates security level,color and guage options
  useEffect(() => {
    if (finalScore === 0) {
      setSeries(0);
    } else if (finalScore > 0 && finalScore <= 3.99) {
      setSeries(3);
    } else if (finalScore >= 4 && finalScore <= 6.99) {
      setSeries(5);
    } else if (finalScore >= 7 && finalScore <= 8.99) {
      setSeries(7);
    } else if (finalScore >= 9) {
      setSeries(9);
    } else {
      setSeries([parseFloat(finalScore)]);
    }

    const severity = getSeverity(finalScore);

    if (severity){
      setSecurityLevel(severity ? severity.label : null);
      setSecurityColor(getSecurityColor(severity.slug));
      guageOptions.series[0].detail.backgroundColor.image = getEmoji(finalScore);
      guageOptions.series[0].data[0].value = 5;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalScore]);
  
  return (
    <div className="my-md text-center">
      <div>
        <div className="risk-posture-title">Risk Posture</div>
        <div className="sub-title" style={{ color: securityColor }}>
          {securityLevel}
        </div>
        <div className="guage-chart">
          <EChartsReact
            className='vchart'
            option={guageOptions}
            notMerge={true}
            lazyUpdate={true}
          />
        </div>
      </div>
    </div>
  );
};

export default Graph;
